import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'

import { ProductModal as F0ProductModal } from '@factorialco/f0-react'
import { Spinner } from '@factorialco/f0-react/dist/experimental'
import Envelope from '@factorialco/f0-react/icons/app/Envelope'
import Share from '@factorialco/f0-react/icons/app/Share'
import Upsell from '@factorialco/f0-react/icons/app/Upsell'
import { CanSeeBlackFridayPromoDocument } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { useCreateEmailModuleRequest } from 'modules/marketplace_modules/hooks/useCreateEmailModuleRequest'
import { moduleAvailableToModuleId } from 'modules/upsell/lib/moduleIcons'
import { UpsellGuard } from 'modules/upselling_kit/components/UpsellGuard'
import { useGetUpsellingData } from 'modules/upselling_kit/hooks/useGetUpsellingData'
import { UpsellingKitModule } from 'modules/upselling_kit/types'
import { useDefaultMessages } from 'modules/upselling_kit/utils/defaultMessages'
import {
  handleUpsellingLearnMore,
  handleUpsellingOnClose,
  handleUpsellingVisibility,
} from 'modules/upselling_kit/utils/handlers'

interface LearnMoreNavigation {
  url: string
  isExternal: boolean
}

interface ProductModalProps extends UpsellingKitModule {
  modalTitle?: string
  title?: string
  isOpen: boolean
  onClose: () => void
  imageUrl?: string
  pageSource: string
  onRequest?: () => void
  openFromUpsellButton?: boolean
  image?: string
  benefits?: string[]
  learnMoreNavigate?: string | LearnMoreNavigation
  upsellRequestComponentType?: string
}

const ProductModalContent = ({
  moduleRequested,
  pageSource: _pageSource,
  title,
  isOpen,
  onClose,
  requestSource,
  modalTitle,
  openFromUpsellButton,
  image,
  benefits,
  learnMoreNavigate,
  onRequest,
}: ProductModalProps) => {
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const {
    moduleName,
    upsellModalDescription,
    upsellModalImage: defaultUpsellModalImage,
    upsellModalBenefits: defaultUpsellModalBenefits,
    moduleSlug,
    loading,
  } = useGetUpsellingData({
    moduleRequested,
    title,
  })
  const defaultMessages = useDefaultMessages()
  const { create: createEmailRequest } = useCreateEmailModuleRequest()
  const { company } = useCurrentSession()
  const { data } = useQuery(CanSeeBlackFridayPromoDocument)
  const canSeePromo =
    data?.upsell._policies.canSeeBlackFridayPromo.can && !company.subscription?.inFreeTrial

  const getOrCreatePortalContainer = () => {
    let container = document.getElementById('high-zindex-modal-portal')
    if (!container) {
      container = document.createElement('div')
      container.id = 'high-zindex-modal-portal'
      container.style.position = 'fixed'
      container.style.top = '0'
      container.style.left = '0'
      container.style.width = '100%'
      container.style.height = '100%'
      container.style.zIndex = '1300'
      container.style.pointerEvents = 'none'
      document.body.appendChild(container)
    }
    return container
  }

  useEffect(() => {
    if (isOpen && !openFromUpsellButton) {
      handleUpsellingVisibility({
        moduleRequested,
        requestSource,
        component: 'product_modal',
      })
    } else if (isOpen && openFromUpsellButton) {
      track(trackingEvents.upsellingKit.openProductModal, {
        source: requestSource,
        module: moduleRequested,
        component: 'product_modal',
      })
    }
  }, [isOpen, moduleRequested, requestSource, openFromUpsellButton])

  if (loading) {
    return <Spinner />
  }

  const handleOnClose = () => {
    handleUpsellingOnClose({
      onClose,
      requestSource,
      moduleRequested,
      component: 'product_modal',
    })
  }

  const handleLearnMore = () => {
    handleUpsellingLearnMore({
      moduleRequested,
      requestSource,
      component: 'product_modal',
    })

    if (learnMoreNavigate) {
      if (typeof learnMoreNavigate === 'string') {
        navigate(learnMoreNavigate)
      } else {
        const { url, isExternal } = learnMoreNavigate
        if (isExternal) {
          window.open(url, '_blank')
        } else {
          navigate(url)
        }
      }
    } else {
      navigate(
        navigation.discover.marketplaceModules.item.fullPath({
          id: moduleSlug || '',
        })
      )
    }
    onClose()
  }

  const buildPrimaryAction = () => {
    return {
      label: i18n.t('upselling_kit.button.learn_more'),
      onClick: handleLearnMore,
      variant: 'default' as const,
      icon: Share,
    }
  }

  const handleSendEmail = async () => {
    try {
      await createEmailRequest({
        moduleName: moduleRequested,
      })
      onRequest?.()
      app.setFlash({
        type: 'correct',
        message: i18n.t('upselling_kit.button.email_sent'),
      })
      onClose()
      track(trackingEvents.upsellingKit.sendEmail, {
        source: requestSource,
        module: moduleRequested,
        component: 'product_modal',
      })
    } catch (error) {}
  }

  const buildSecondaryAction = () => {
    return {
      label: i18n.t('upselling_kit.button.send_email'),
      onClick: handleSendEmail,
      variant: 'outline' as const,
      icon: Envelope,
    }
  }

  const moduleId = moduleAvailableToModuleId(moduleRequested)

  const upsellTag = {
    label: i18n.t('upselling_kit.tag_label'),
    icon: Upsell,
  }

  const promoTag = {
    label: i18n.t('marketplace.revamp.list.discount', { percentage: '50' }),
    variant: 'warning' as const,
  }

  return (
    <F0ProductModal
      isOpen={isOpen}
      showResponseDialog
      onClose={handleOnClose}
      modalTitle={modalTitle || moduleName || ''}
      modalModule={moduleId}
      title={title || upsellModalDescription || ''}
      image={image || defaultUpsellModalImage || ''}
      benefits={benefits || defaultUpsellModalBenefits || []}
      primaryAction={buildPrimaryAction()}
      secondaryAction={buildSecondaryAction()}
      errorMessage={defaultMessages.error}
      successMessage={defaultMessages.success}
      loadingState={defaultMessages.loading}
      closeLabel={defaultMessages.close}
      portalContainer={getOrCreatePortalContainer()}
      tag={upsellTag}
      promoTag={canSeePromo ? promoTag : undefined}
    />
  )
}

const ProductModal = ({
  fallbackComponent,
  title,
  isOpen,
  onClose,
  modalTitle,
  openFromUpsellButton = true,
  image,
  benefits,
  learnMoreNavigate,
  ...upsellProps
}: ProductModalProps) => {
  return (
    <UpsellGuard {...upsellProps} fallbackComponent={fallbackComponent || null}>
      <ProductModalContent
        {...upsellProps}
        isOpen={isOpen}
        onClose={onClose}
        title={title}
        modalTitle={modalTitle}
        openFromUpsellButton={openFromUpsellButton}
        image={image}
        benefits={benefits}
        learnMoreNavigate={learnMoreNavigate}
      />
    </UpsellGuard>
  )
}

export { ProductModal }
